import { useState, useEffect, useCallback } from "react";
import { authFetch, API, useAuth } from "@/App";
import {
  Factory, Truck, FlaskConical, Gauge, Package,
  ShieldCheck, Zap, TrendingUp, AlertTriangle, RefreshCw,
  Users, DollarSign, FileInput, Warehouse, ExternalLink,
} from "lucide-react";
import { Link } from "react-router-dom";

const StatCard = ({ icon: Icon, label, value, unit, trend, color, testId }) => (
  <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5 flex flex-col gap-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      {trend !== undefined && trend !== null && (
        <span className={`text-xs font-medium flex items-center gap-1 ${trend > 0 ? "text-emerald-400" : trend < 0 ? "text-red-400" : "text-slate-400"}`}>
          <TrendingUp className={`w-3 h-3 ${trend < 0 ? "rotate-180" : ""}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div>
      <p className="text-slate-400 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white text-2xl font-bold mt-1">
        {value} <span className="text-sm text-slate-500 font-normal">{unit}</span>
      </p>
    </div>
  </div>
);

const EquipmentCard = ({ eq }) => (
  <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-4" data-testid={`equipment-${eq.equipment_id}`}>
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-medium text-sm">{eq.name}</h3>
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
        eq.status === "RUNNING" ? "bg-emerald-600/20 text-emerald-400" :
        eq.status === "MAINTENANCE" ? "bg-amber-600/20 text-amber-400" :
        eq.status === "IDLE" ? "bg-slate-600/20 text-slate-400" :
        "bg-red-600/20 text-red-400"
      }`}>{eq.status}</span>
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div>
        <p className="text-slate-500 text-[10px] uppercase">Type</p>
        <p className="text-white text-sm font-semibold">{eq.type}</p>
      </div>
      <div>
        <p className="text-slate-500 text-[10px] uppercase">Run Hours</p>
        <p className="text-white text-sm font-semibold">{eq.hours_run?.toLocaleString()}</p>
      </div>
      <div>
        <p className="text-slate-500 text-[10px] uppercase">Health</p>
        <p className="text-white text-sm font-semibold">{eq.health}%</p>
      </div>
    </div>
    <div className="mt-3 h-1.5 bg-slate-700 rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all ${eq.health > 85 ? "bg-emerald-500" : eq.health > 70 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${eq.health}%` }} />
    </div>
  </div>
);

const DISPATCH_STATUS_COLORS = {
  PENDING: "bg-slate-500", WAITING: "bg-amber-500", LOADING: "bg-blue-500",
  READY: "bg-cyan-500", DISPATCHED: "bg-emerald-500", CANCELLED: "bg-red-500",
};

const STEPS = ["PENDING", "WAITING", "LOADING", "READY", "DISPATCHED"];

const DispatchStatusBar = ({ status }) => {
  const idx = STEPS.indexOf(status);
  return (
    <div className="flex items-center gap-0.5 mt-2">
      {STEPS.map((s, i) => (
        <div key={s} className="flex-1 flex flex-col items-center">
          <div className={`h-1.5 w-full rounded-full ${i <= idx ? DISPATCH_STATUS_COLORS[s] : "bg-slate-700"}`} />
          <span className={`text-[8px] mt-0.5 ${i <= idx ? "text-slate-300" : "text-slate-600"}`}>{s}</span>
        </div>
      ))}
    </div>
  );
};

export default function FactoryDashboard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const userUnit = user?.assigned_unit || null;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeUnit, setActiveUnit] = useState(isSuperAdmin ? "all" : (userUnit || "all"));

  const loadDashboard = useCallback(async () => {
    try {
      const unitParam = activeUnit !== "all" ? `?unit=${activeUnit}` : "";
      const res = await authFetch(`${API}/factory/dashboard${unitParam}`);
      const json = await res.json();
      if (json.success) setData(json.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeUnit]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  useEffect(() => {
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, [loadDashboard]);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="factory-dashboard-loading">
      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const d = data || {};
  const prod = d.production || {};
  const dispatch = d.dispatch || {};
  const qc = d.qc || {};
  const eq = d.equipment || {};
  const mat = d.materials || {};
  const fg = d.finished_goods || {};
  const safety = d.safety || {};
  const energy = d.energy || {};
  const manpower = d.manpower || {};
  const expenses = d.expenses || {};
  const inventory = d.inventory || { items: [], alerts: [] };

  const unitLabel = isSuperAdmin
    ? (activeUnit === "all" ? "All Units" : activeUnit)
    : (userUnit || "Factory");

  const handleOrderClick = (orderId) => {
    if (isSuperAdmin) {
      window.open(`/orders?highlight=${orderId}`, "_blank");
    }
  };

  return (
    <div className="space-y-6" data-testid="factory-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Sora']">
            {userUnit && !isSuperAdmin ? `${userUnit} Dashboard` : "Factory Dashboard"}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {unitLabel} — Real-time overview (auto-refreshes)
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && ["all", "Unit 1", "Unit 2"].map((u) => (
            <button key={u} onClick={() => { setActiveUnit(u); setLoading(true); }}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeUnit === u ? "bg-emerald-600 text-white" : "bg-[#1C2B2B] text-slate-400 hover:text-white border border-emerald-900/30"}`}
              data-testid={`unit-tab-${u.replace(" ", "-").toLowerCase()}`}
            >{u === "all" ? "All Units" : u}</button>
          ))}
          <button onClick={loadDashboard} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600/20 text-emerald-400 rounded-lg text-sm hover:bg-emerald-600/30 transition-colors ml-2" data-testid="refresh-dashboard">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Factory} label="Daily Production" value={prod.daily_actual?.toLocaleString() || "0"} unit="MT" trend={prod.efficiency ? Math.round(prod.efficiency - 90) : null} color="bg-emerald-600" testId="kpi-production" />
        <StatCard icon={Truck} label="Dispatches" value={dispatch.total_today || 0} unit={`(${dispatch.pending || 0} pending)`} color="bg-blue-600" testId="kpi-dispatches" />
        <StatCard icon={FlaskConical} label="QC Pass Rate" value={qc.pass_rate || 100} unit="%" color="bg-violet-600" testId="kpi-qc-rate" />
        <StatCard icon={Package} label="Finished Stock" value={fg.total_mt?.toLocaleString() || "0"} unit="MT" color="bg-teal-600" testId="kpi-stock" />
        <StatCard icon={Users} label="Workers Today" value={manpower.today_total || 0} unit="" color="bg-amber-600" testId="kpi-manpower" />
        <StatCard icon={DollarSign} label="Month Expenses" value={expenses.monthly_total ? `${(expenses.monthly_total/100000).toFixed(1)}L` : "0"} unit="Rs." color="bg-rose-600" testId="kpi-expenses" />
      </div>

      {/* Equipment Status */}
      <div>
        <h2 className="text-white font-semibold mb-3 text-lg">Equipment Status ({eq.running || 0}/{eq.total || 0} Running)</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {(eq.items || []).map((item) => (
            <EquipmentCard key={item.equipment_id} eq={item} />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dispatch Queue with Status Bar */}
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="dispatch-queue-widget">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Truck className="w-4 h-4 text-emerald-400" /> Dispatch Queue ({dispatch.pending || 0} pending)
          </h3>
          <div className="space-y-3">
            {(dispatch.recent || []).map((item) => (
              <div key={item.dispatch_id}
                className={`bg-[#0F1A1A] rounded-lg p-3 ${(isSuperAdmin || user?.role === "FACTORY_ADMIN") ? "cursor-pointer hover:bg-[#142020] transition-colors" : ""}`}
                onClick={() => handleOrderClick(item.order_id)}
                data-testid={`dispatch-item-${item.dispatch_id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{item.dealer_name || item.order_id}</p>
                    <p className="text-slate-400 text-xs truncate">{item.product_summary} - {item.total_bags} bags</p>
                  </div>
                  <div className="text-right ml-3 flex items-center gap-2">
                    {item.priority === "URGENT" && <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-600/20 text-red-400">URGENT</span>}
                    <span className="text-[10px] text-slate-500">{item.order_id}</span>
                  </div>
                </div>
                <DispatchStatusBar status={item.status} />
              </div>
            ))}
            {(!dispatch.recent || dispatch.recent.length === 0) && (
              <p className="text-slate-500 text-sm text-center py-4">No pending dispatches</p>
            )}
          </div>
        </div>

        {/* Alerts & Stats */}
        <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="material-alerts-widget">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" /> Alerts & Stats
          </h3>
          <div className="space-y-3">
            {mat.critical > 0 && (
              <div className="flex items-start gap-3 bg-[#0F1A1A] rounded-lg p-3">
                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-red-500" />
                <p className="text-red-300 text-sm"><span className="font-semibold">{mat.critical} material(s)</span> below critical level</p>
              </div>
            )}
            {mat.low > 0 && (
              <div className="flex items-start gap-3 bg-[#0F1A1A] rounded-lg p-3">
                <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-amber-500" />
                <p className="text-amber-300 text-sm"><span className="font-semibold">{mat.low} material(s)</span> running low</p>
              </div>
            )}
            <div className="flex items-start gap-3 bg-[#0F1A1A] rounded-lg p-3">
              <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-emerald-500" />
              <p className="text-emerald-300 text-sm"><span className="font-semibold">{safety.days_since_incident || 0}</span> days since last safety incident</p>
            </div>
            <div className="flex items-start gap-3 bg-[#0F1A1A] rounded-lg p-3">
              <div className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-blue-500" />
              <p className="text-blue-300 text-sm">Energy consumption: <span className="font-semibold">{energy.total_mw || 0} MW</span></p>
            </div>
          </div>
        </div>
      </div>

      {/* Live Inventory Snapshot (linked from Inventory module, filtered by active unit) */}
      <div className="bg-[#1C2B2B] border border-emerald-900/30 rounded-xl p-5" data-testid="factory-inventory-widget">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Warehouse className="w-4 h-4 text-emerald-400" /> Live Inventory ({unitLabel})
            {inventory.alerts.length > 0 && (
              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-red-600/20 text-red-300" data-testid="inventory-alert-count">
                {inventory.alerts.length} low-stock alerts
              </span>
            )}
          </h3>
          <Link
            to={`/inventory${activeUnit !== "all" ? `?unit=${encodeURIComponent(activeUnit)}` : ""}`}
            className="text-xs flex items-center gap-1 text-emerald-400 hover:text-emerald-300"
            data-testid="factory-inventory-link"
          >
            Manage Inventory <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        {inventory.items.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-4">No inventory items for this unit</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {inventory.items.slice(0, 9).map((item) => {
              const isLow = (item.current_stock || 0) <= (item.reorder_level || 0);
              const pct = item.max_stock ? Math.min(100, (item.current_stock / item.max_stock) * 100) : 0;
              return (
                <div key={item.item_id} className="bg-[#0F1A1A] rounded-lg p-3" data-testid={`fdash-inv-${item.item_id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-white text-sm font-medium truncate">{item.item_name}</p>
                    {isLow && <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-600/20 text-red-300">LOW</span>}
                  </div>
                  <p className="text-slate-400 text-[10px] uppercase tracking-wide mb-2">
                    {item.category?.replace("_", " ")} · {item.unit || "—"}
                  </p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-white text-lg font-bold">{item.current_stock?.toLocaleString()}</span>
                    <span className="text-slate-500 text-xs">/ {item.max_stock || "—"} {item.uom}</span>
                  </div>
                  {item.max_stock > 0 && (
                    <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${isLow ? "bg-red-500" : pct < 40 ? "bg-amber-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  <p className="text-[9px] text-slate-500 mt-1">Reorder at: {item.reorder_level || 0}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Gauge} label="Production Eff." value={prod.efficiency || 0} unit="%" color="bg-cyan-600" testId="kpi-efficiency" />
        <StatCard icon={ShieldCheck} label="Safety Days" value={safety.days_since_incident || 0} unit="days" color="bg-green-600" testId="kpi-safety" />
        <StatCard icon={Zap} label="Energy Usage" value={energy.total_mw || 0} unit="MW" color="bg-yellow-600" testId="kpi-energy" />
        <StatCard icon={FileInput} label="Total Materials" value={mat.total || 0} unit={`(${mat.critical || 0} critical)`} color="bg-orange-600" testId="kpi-materials" />
      </div>
    </div>
  );
}
